import { useTranslation } from "@redotech/redo-i18n";
import { normalizeLicensePlateScan } from "@redotech/redo-inventory-common/license-plate-scan";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export function formatInventoryItemIdsForDisplay(
  inventoryItemIds: readonly (string | null | undefined)[],
): string[] {
  const displayIds = new Set<string>();
  for (const inventoryItemId of inventoryItemIds) {
    if (!inventoryItemId) {
      continue;
    }
    const rawId = normalizeLicensePlateScan(inventoryItemId);
    if (rawId) {
      displayIds.add(rawId);
    }
  }
  return [...displayIds];
}

const inventoryItemIdsSize = ["xs", "sm", "lg"] as const;
type InventoryItemIdsSize = (typeof inventoryItemIdsSize)[number];

interface InventoryItemIdsProps {
  inventoryItemIds: readonly (string | null | undefined)[];
  size?: InventoryItemIdsSize;
}

export const InventoryItemIds = memo(function InventoryItemIds({
  inventoryItemIds,
  size = "sm",
}: InventoryItemIdsProps) {
  const { t } = useTranslation();
  const displayIds = formatInventoryItemIdsForDisplay(inventoryItemIds);
  if (displayIds.length === 0) {
    return null;
  }

  const fontSize = size === "xs" ? "xxs" : size === "lg" ? "xl" : "sm";
  return (
    <Flex align="baseline" gap="xs" wrap="wrap">
      <Text fontSize={fontSize} fontWeight="medium" textColor="secondary">
        {displayIds.length === 1 ? t("Item ID:") : t("Item IDs:")}
      </Text>
      {displayIds.map((displayId, index) => (
        <Text
          fontFamily="monospace"
          fontSize={fontSize}
          key={displayId}
          textColor="primary"
        >
          {displayId}
          {index < displayIds.length - 1 ? "," : ""}
        </Text>
      ))}
    </Flex>
  );
});
